import { API_ORIGIN, answersFromDraftRows, getHrefWithThreadPreservation } from '@yeshuman/sdk'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import type { Opportunity } from './use-opportunities'
import { useMyApplication, type MyApplication } from './use-my-applications'

import {
  type ApplicationAnswerValue,
  type ScreeningQuestion,
} from '../components/candidate/candidate-featured-jobs-types'

export type UseApplicationApplyFlowOptions = {
  onSuccess?: () => void
}

async function fetchScreeningQuestions(opportunityId: string): Promise<ScreeningQuestion[]> {
  const token = localStorage.getItem('auth_token')
  const response = await fetch(`${API_ORIGIN}/api/opportunities/${opportunityId}/questions`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) return []
  const parsed = (await response.json()) as ScreeningQuestion[]
  return Array.isArray(parsed) ? parsed : []
}

async function openApplicationDraft(opportunityId: string): Promise<MyApplication> {
  const token = localStorage.getItem('auth_token')
  const response = await fetch(`${API_ORIGIN}/api/applications/draft`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ opportunity_id: opportunityId }),
  })
  if (!response.ok) {
    const err = (await response.json().catch(() => ({}))) as { error?: string }
    throw new Error(err.error || 'Could not open application')
  }
  return (await response.json()) as MyApplication
}

function validateAnswers(
  screeningQuestions: ScreeningQuestion[],
  answers: Record<string, ApplicationAnswerValue>,
): boolean {
  for (const question of screeningQuestions) {
    if (!question.is_required) continue
    const answer = answers[question.id]
    if (answer === undefined || answer === null || answer === '') return false
    if (Array.isArray(answer) && answer.length === 0) return false
  }
  return true
}

function applyServerDraft(
  draft: MyApplication,
  questions: ScreeningQuestion[],
  setCoverLetter: (value: string) => void,
  setAnswers: (value: Record<string, ApplicationAnswerValue>) => void,
  lastCoverRef: { current: string },
  lastAnswersKeyRef: { current: string },
) {
  const cover = draft.cover_letter ?? ''
  const mapped = answersFromDraftRows(draft.answers, questions)
  const answersKey = JSON.stringify(mapped)
  if (cover !== lastCoverRef.current) {
    lastCoverRef.current = cover
    setCoverLetter(cover)
  }
  if (answersKey !== lastAnswersKeyRef.current) {
    lastAnswersKeyRef.current = answersKey
    setAnswers(mapped)
  }
}

export function useApplicationApplyFlow(
  opportunity: Opportunity | null,
  options: UseApplicationApplyFlowOptions = {},
) {
  const { onSuccess } = options
  const navigate = useNavigate()

  const [applicationId, setApplicationId] = useState<string | null>(null)
  const [screeningQuestions, setScreeningQuestions] = useState<ScreeningQuestion[]>([])
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false)
  const [answers, setAnswers] = useState<Record<string, ApplicationAnswerValue>>({})
  const [coverLetter, setCoverLetter] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const lastCoverRef = useRef('')
  const lastAnswersKeyRef = useRef('')

  const liveApplication = useMyApplication(applicationId ?? undefined)

  const reset = useCallback(() => {
    setApplicationId(null)
    setScreeningQuestions([])
    setAnswers({})
    setCoverLetter('')
    setIsSubmitting(false)
    setIsLoadingQuestions(false)
    lastCoverRef.current = ''
    lastAnswersKeyRef.current = ''
  }, [])

  useEffect(() => {
    if (!opportunity?.id) {
      reset()
      return
    }

    let cancelled = false
    setIsLoadingQuestions(true)
    setAnswers({})
    setCoverLetter('')
    lastCoverRef.current = ''
    lastAnswersKeyRef.current = ''

    void Promise.all([fetchScreeningQuestions(opportunity.id), openApplicationDraft(opportunity.id)])
      .then(([questions, draft]) => {
        if (cancelled) return
        setScreeningQuestions(questions)
        setApplicationId(draft.id)
        applyServerDraft(
          draft,
          questions,
          setCoverLetter,
          setAnswers,
          lastCoverRef,
          lastAnswersKeyRef,
        )
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setScreeningQuestions([])
        toast.error(error instanceof Error ? error.message : 'Could not open application')
      })
      .finally(() => {
        if (!cancelled) setIsLoadingQuestions(false)
      })

    return () => {
      cancelled = true
    }
  }, [opportunity?.id, reset])

  useEffect(() => {
    const draft = liveApplication.data
    if (!draft || draft.status !== 'draft') return
    applyServerDraft(
      draft,
      screeningQuestions,
      setCoverLetter,
      setAnswers,
      lastCoverRef,
      lastAnswersKeyRef,
    )
  }, [liveApplication.data, screeningQuestions])

  const onAnswerChange = useCallback((questionId: string, value: ApplicationAnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }))
  }, [])

  const submit = useCallback(async () => {
    if (!opportunity) return

    if (screeningQuestions.length > 0 && !validateAnswers(screeningQuestions, answers)) {
      toast.error('Please answer all required questions')
      return
    }

    setIsSubmitting(true)
    try {
      const token = localStorage.getItem('auth_token')

      const requestBody: Record<string, unknown> = {
        opportunity_id: opportunity.id,
      }

      const trimmedCover = coverLetter.trim()
      if (trimmedCover) requestBody.cover_letter = trimmedCover

      if (screeningQuestions.length > 0) {
        requestBody.answers = Object.entries(answers).map(([questionId, value]) => {
          const answerText =
            typeof value === 'string'
              ? value
              : typeof value === 'boolean'
                ? value
                  ? 'true'
                  : 'false'
                : typeof value === 'number'
                  ? String(value)
                  : ''
          const answerOptions = Array.isArray(value) ? value : []
          return {
            question_id: questionId,
            answer_text: answerText,
            answer_options: answerOptions,
          }
        })
      }

      const response = await fetch(`${API_ORIGIN}/api/applications/apply`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      })

      if (response.ok) {
        toast.success("Application submitted — it's in Applied", {
          action: {
            label: 'View',
            onClick: () => navigate(getHrefWithThreadPreservation('/candidate/jobs?tab=applied')),
          },
        })
        reset()
        onSuccess?.()
      } else {
        const err = await response.json().catch(() => ({}))
        toast.error((err as { error?: string }).error || 'Failed to submit application')
      }
    } catch {
      toast.error('Failed to submit application')
    } finally {
      setIsSubmitting(false)
    }
  }, [answers, coverLetter, navigate, onSuccess, opportunity, reset, screeningQuestions])

  return {
    applicationId,
    screeningQuestions,
    isLoadingQuestions,
    answers,
    coverLetter,
    setCoverLetter,
    isSubmitting,
    onAnswerChange,
    submit,
    reset,
  }
}
